import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { toast } from "@/components/ui/use-toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  regionLabel,
  regionOptions,
  useAgentServerRegions,
  useOrganizationDefaultRegion,
} from "@/hooks/useAgentServerRegions";
import {
  fetchLatestProbe,
  fetchRecentCallMetrics,
  formatMs,
  formatTotal,
  LatencyProbeError,
  pickSource,
  probeFromResponse,
  runLatencyProbe,
  segmentShares,
  sourceLabel,
  type LatencySource,
  type LatencyStage,
  type ProbeRow,
} from "@/services/latencyService";
import { buildProbeOverrides } from "./providerConfigs";

interface LatencyCardProps {
  assistantId: string;
  // The assistant's organization, whose default region applies when the assistant has none.
  organizationId?: string;
}

const STAGES: { stage: LatencyStage; name: string; color: string }[] = [
  { stage: "stt", name: "Transcriber", color: "bg-orange-500" },
  { stage: "llm", name: "LLM", color: "bg-blue-500" },
  { stage: "tts", name: "Voice", color: "bg-purple-500" },
];

// A stage that failed in a probe still gets a visible slice.
const ERROR_SHARE = 0.08;

const HATCHED =
  "bg-muted [background-image:repeating-linear-gradient(45deg,transparent,transparent_3px,hsl(var(--muted-foreground)/0.35)_3px,hsl(var(--muted-foreground)/0.35)_5px)]";

// "deepgram", "flux-general-en" -> "Deepgram · flux-general-en"
const capitalise = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const modelLabel = (provider?: string, model?: string) =>
  [provider ? capitalise(provider) : "", model].filter(Boolean).join(" · ");

export default function LatencyCard({ assistantId, organizationId }: LatencyCardProps) {
  const { watch, getValues } = useFormContext();
  const { liveRegions } = useAgentServerRegions();
  const orgDefault = useOrganizationDefaultRegion(organizationId);

  // The card's own pick. It never writes back to the form or the saved assistant.
  const [picked, setPicked] = useState<string | null>(null);
  const saved: string = watch("agent_server_region") || "";
  const region = picked ?? (saved || orgDefault.region);

  // From the database (calls, else a stored probe) for the selected region; null while loading.
  const [loaded, setLoaded] = useState<LatencySource | null>(null);
  // The last probe response, shown as is until the region changes (the API saves nothing when
  // every stage failed, so it cannot always be read back).
  const [fresh, setFresh] = useState<{ region: string; row: ProbeRow } | null>(null);
  const [probing, setProbing] = useState(false);
  const source = fresh && fresh.region === region ? pickSource([], fresh.row) : loaded;

  const load = useCallback(async () => {
    if (!region) {
      setLoaded({ kind: "none" });
      return;
    }
    try {
      const calls = await fetchRecentCallMetrics(assistantId, region);
      // The probe is only read when no call has data for the region.
      const afterCalls = pickSource(calls, null);
      setLoaded(
        afterCalls.kind === "calls"
          ? afterCalls
          : pickSource(calls, await fetchLatestProbe(assistantId, region)),
      );
    } catch (error) {
      console.error("Failed to load latency:", error);
      setLoaded({ kind: "none" });
    }
  }, [assistantId, region]);

  useEffect(() => {
    setLoaded(null);
    load();
  }, [load]);

  const probe = async () => {
    setProbing(true);
    try {
      const result = await runLatencyProbe(assistantId, region, buildProbeOverrides(getValues()));
      setFresh({ region, row: probeFromResponse(result, region) });
    } catch (error) {
      if (error instanceof LatencyProbeError && error.code === "probe_rate_limited") {
        toast({
          title: "Probe rate limited",
          description: error.retryAfter
            ? `Try again in ${error.retryAfter}s.`
            : "Try again in a moment.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Latency check failed",
          description: error instanceof Error ? error.message : "Unknown error",
          variant: "destructive",
        });
      }
    } finally {
      setProbing(false);
    }
  };

  const options = useMemo(
    () =>
      regionOptions(liveRegions, region).map((code) => ({ value: code, label: regionLabel(code) })),
    [liveRegions, region],
  );

  // What the tooltips call each stage's model: the saved probe's labels, else the form's.
  const formLabels: Record<LatencyStage, string> = {
    stt: modelLabel(watch("stt_provider"), watch("stt_model")),
    llm: modelLabel(watch("llm_provider"), watch("llm_model")),
    tts: modelLabel(watch("tts_provider"), watch("tts_model")),
  };

  const hasData = source !== null && source.kind !== "none";
  const breakdown = hasData ? source.breakdown : null;
  const errors = source?.kind === "probe" ? source.errors : {};
  const shares = breakdown ? segmentShares(breakdown) : null;
  // A probe where every stage failed: nothing to total, the tooltips carry the errors.
  const allFailed = Object.keys(errors).length === 3;

  const tooltipFor = (stage: LatencyStage, name: string) => {
    if (errors[stage]) return `${name}: ${errors[stage]}`;
    const label = (source?.kind === "probe" && source.labels[stage]) || formLabels[stage];
    const ms = breakdown[stage];
    let text = `${name}${label ? ` · ${label}` : ""}: ${formatMs(ms)}.`;
    if (stage === "llm" && breakdown.llmTtft !== null) {
      text += ` ttft ${formatMs(breakdown.llmTtft)} + buffer ${formatMs(breakdown.llmBuffer ?? 0)}`;
    }
    return text;
  };

  return (
    <div className="mb-4 rounded-lg border bg-card p-3 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-sm text-muted-foreground underline decoration-dotted underline-offset-4">
                Latency
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              Time from the caller finishing a sentence to the agent starting to speak: speech
              recognition delay, LLM first token and buffering, and voice first byte.
            </TooltipContent>
          </Tooltip>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-semibold tabular-nums">
              {hasData && !allFailed ? formatTotal(breakdown.total) : "--"}
            </span>
            <span className="text-sm text-muted-foreground">ms</span>
          </div>
        </div>
        <div className="w-56">
          <Combobox
            options={options}
            value={region}
            onValueChange={setPicked}
            placeholder="Select region"
            searchPlaceholder="Search regions..."
            emptyText="No regions found."
          />
        </div>
      </div>

      <div className="mt-2 flex h-3 gap-0.5 overflow-hidden rounded-full">
        {hasData
          ? STAGES.map(({ stage, name, color }) => {
              const failed = !!errors[stage];
              const share = failed ? ERROR_SHARE : shares[stage];
              if (share <= 0) return null;
              return (
                <Tooltip key={stage}>
                  <TooltipTrigger asChild>
                    <div
                      className={failed ? HATCHED : color}
                      style={{ flexGrow: share, flexBasis: 0 }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>{tooltipFor(stage, name)}</TooltipContent>
                </Tooltip>
              );
            })
          : <div className="flex-1 rounded-full bg-muted" />}
      </div>

      <div className="mt-1.5 flex min-h-7 items-center gap-1 text-xs text-muted-foreground">
        {source === null ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : hasData ? (
          <>
            <span>
              {allFailed
                ? "All stages failed, nothing saved. Hover a segment for the error."
                : sourceLabel(source)}
            </span>
            {source.kind === "probe" && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={probe}
                disabled={probing}
                aria-label="Check latency again"
              >
                {probing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3" />
                )}
              </Button>
            )}
          </>
        ) : (
          <>
            <span>{region ? "No calls or probes from this region yet." : "Pick a region."}</span>
            {region && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={probe}
                disabled={probing}
              >
                {probing && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                Check latency
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
